// *********************************************************************
// Copyright (c) 2019-2024 Markadoc Corp and Ontario Partners LLC of NY.
// All Rights Reserved
// *********************************************************************
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { catchError, shareReplay, tap, map } from "rxjs/operators";

import { DocumentService } from "./document.service";
import { GroupService } from "./group.service";
import { OrgUserService } from "./orguser.service";

import { OrgData } from "../org/model/orgdata.model";
import { CountData } from "../model/countdata.model";
import { SumData } from "../model/sumdata.model";
import { StatData } from "../model/statdata.model";
import { AvgData } from "../model/avgdata.model";
import { ToasterService } from "../service/toaster.service";
import { ToastrModule, ToastrService } from "ngx-toastr";

@Injectable({
  providedIn: "root",
})
export class OrganizationService {
  baseUrl = environment.apiUrl;

  //Observables
  orgUserCount$: Observable<any> = null;
  orgEmpCount$: Observable<any> = null;
  orgActiveEmpCount$: Observable<any> = null;
  orgAvailableEmpCount$: Observable<any> = null;
  orgGuestCount$: Observable<any> = null;
  orgActiveGuestCount$: Observable<any> = null;
  orgAvailableGuestCount$: Observable<any> = null;
  orgGroupCount$: Observable<any> = null;
  orgDocCount$: Observable<any> = null;
  orgStorage$: Observable<any> = null;

  public Org: OrgData = null;

  public OrgList: OrgData[] = null;
  public newOrgList: OrgData[] = null;

  public OrgSystemCnt: CountData = null;
  public OrgSystemStorage: SumData = null;

  public OrgStorage: SumData = null;

  // public OrgAvg: AvgData = null;

  public orgListItems$: BehaviorSubject<OrgData[]> = new BehaviorSubject<
    OrgData[]
  >(this.OrgList);
  public newOrgListItems$: BehaviorSubject<OrgData[]> = new BehaviorSubject<
    OrgData[]
  >(this.newOrgList);

  //  private orgUserInfoItems$: BehaviorSubject<OrgData[]> = new BehaviorSubject<OrgData[]>([]);

  private orgItem$: BehaviorSubject<OrgData> = new BehaviorSubject<OrgData>(
    this.Org,
  );

  orgList$: Observable<OrgData[]> = this.orgListItems$.asObservable();

  newOrgList$: Observable<OrgData[]> = this.newOrgListItems$.asObservable();

  orgData$: Observable<OrgData> = this.orgItem$.asObservable();

  private orgSystemCountSubj$: BehaviorSubject<CountData> =
    new BehaviorSubject<CountData>(this.OrgSystemCnt);
  orgSystemCount$: Observable<CountData> =
    this.orgSystemCountSubj$.asObservable();

  private orgStatusCountSubj$: BehaviorSubject<StatData[]> =
    new BehaviorSubject<StatData[]>(null);
  orgStatusCount$: Observable<StatData[]> =
    this.orgStatusCountSubj$.asObservable();

  constructor(
    private http: HttpClient,
    private toast: ToasterService,
    private documentsvc: DocumentService,
    private groupsvc: GroupService,
    private orgusersvc: OrgUserService,
  ) {}

  public getOrgListData() {
    return this.orgListItems$.value;
  }

  public getOrgItemData() {
    return this.orgItem$.value;
  }

  public getOrganization$(oid: string): Observable<OrgData> {
    return this.http.get<OrgData>(this.baseUrl + "/org/uid/" + oid);
    //            .pipe(tap((res) => {
    //                return res as OrgData;
    //            }));
  }

  public getOrganizationData$(oid: string) {
    this.http.get<OrgData>(this.baseUrl + "/org/uid/" + oid).subscribe((res) => {
      const item = this.orgItem$.next(res);
      console.log("org = ", res);
      return item;
    });
  }

  removeListItem(o: any, list: any[]) {
    const index = list.findIndex((x) => x.uid === o.uid);
    list.splice(index, 1);
  }

  updateListItem(obj, nobj) {
    for (const key in nobj) {
      if (key != "uid") {
        obj[key] = nobj[key];
      }
    }
  }

  processListChanges(list, newList) {
    // Need to remove any items in list not in newList..
    // console.log('org processList length=', list.length);
    // console.log('org processList newList=', newList);
    if (list && list.length > 0 && newList) {
      for (const o of list) {
        const nobj = newList.find((x) => x.uid === o.uid);
        // console.log('processList nobj=', nobj);
        if (!nobj) {
          this.removeListItem(o, list);
        }
      }
      // compare and update obj if necessary
      for (const obj of list) {
        // console.log ('ou update obj=', obj);
        const nobj = newList.find((x) => x.uid === obj.uid);
        if (JSON.stringify(obj) !== JSON.stringify(nobj)) {
          this.updateListItem(obj, nobj);
        }
      }
      // Add any items from newlist not in olist
      const newItems = newList.filter((obj) =>
        list.every((g) => g.uid !== obj.uid),
      );
      // console.log('ou processList: newItems=', newItems);
      list.push(...newItems);
    }
    return list;
  }

  getAllOrganizations$() {
    this.http.get<OrgData[]>(this.baseUrl + "/org").subscribe((res) => {
      /***************
                       this.OrgList = res;
                       this.orgListItems$.next(this.OrgList);
                       console.log('org list =', res);
                       // return res as OrgData[];
                       return this.OrgList;
                       ***********/
      // console.log('org list =', res);
      this.newOrgList = res;
      if (!this.OrgList) {
        this.OrgList = [];
      }
      if (this.OrgList.length === 0 && this.newOrgList) {
        this.OrgList = Array.from(this.newOrgList);
      } else {
        this.processListChanges(this.OrgList, this.newOrgList);
      }
      this.OrgList = Object.assign([], this.OrgList);
      this.orgListItems$.next(this.OrgList);
      return this.OrgList;
    });
  }

  getAllOrganizationsPromise() {
    return this.http
      .get<any[]>(this.baseUrl + "/org")
      .toPromise()
      .then(
        (res) => {
          /************************
                   // console.log('org list =', res);
                   this.OrgList = res;
                   this.orgListItems$.next(this.OrgList);
                   // return res as OrgData[];
                   return this.OrgList;
                   ***********************/
          this.newOrgList = res;
          if (!this.OrgList) {
            this.OrgList = [];
          }
          if (this.OrgList.length === 0 && this.newOrgList) {
            this.OrgList = Array.from(this.newOrgList);
          } else {
            this.OrgList = this.processListChanges(
              this.OrgList,
              this.newOrgList,
            );
          }
          this.OrgList = Object.assign([], this.OrgList);
          this.orgListItems$.next(this.OrgList);
          return this.OrgList;
        },
        (e) => {
          console.log("orgsvc failed to get all orgs promise, error: ", e);
          throw e;
        },
      );
  }

  getOrganizationPromise(oid: string): Promise<any> {
    return this.http
      .get<OrgData>(this.baseUrl + "/org/uid/" + oid)
      .toPromise()
      .then(
        (res) => {
          console.log("org = ", res);
          this.Org = res;
          this.orgItem$.next(res);
          return res as OrgData;
        },
        (e) => {
          console.log("orgsvc failed to get org promise, error: ", e);
          throw e;
        },
      );
  }

  async getOrganization(oid: string): Promise<any> {
    try {
      const org = await this.getOrganizationPromise(oid);
      return org;
    } catch (e) {
      console.error("getOrganization error e=", e);
      throw e;
    }
  }

  getAllOrganizations() {
    return this.http.get<OrgData[]>(this.baseUrl + "/org");
  }

  getUserOrganizationsObs$(uid: string) {
    return this.http.get<OrgData[]>(this.baseUrl + "/org/userorgs/" + uid);
  }

  getUserOrganizations$(uid: string) {
    this.http
      .get<OrgData[]>(this.baseUrl + "/org/userorgs/" + uid)
      .subscribe((res) => {
        // console.log('user org list =', res);
        /*****
                         this.OrgList = res;
                         this.orgListItems$.next(this.OrgList);
                         // return res as OrgData[];
                         return this.OrgList;
                         *******/
        if (!this.OrgList) {
          this.OrgList = [];
        }
        // console.log('user org list =', res);
        this.newOrgList = res;
        if (this.OrgList.length === 0 && this.newOrgList) {
          this.OrgList = Array.from(this.newOrgList);
        } else {
          this.processListChanges(this.OrgList, this.newOrgList);
        }
        this.OrgList = Object.assign([], this.OrgList);
        this.orgListItems$.next(this.OrgList);
        return this.OrgList;
      });
  }

  getUserOrganizationsPromise(uid: string) {
    return this.http
      .get<any[]>(this.baseUrl + "/org/userorgs/" + uid)
      .toPromise()
      .then(
        (res) => {
          // this.currentOrgList = res;
          // May need to merge orglist array with res here
          /*****************
                     this.OrgList = res;
                     this.orgListItems$.next(this.OrgList);
                     // return res as OrgData[];
                     return this.OrgList;
                     ****************/
          this.newOrgList = res;
          if (!this.OrgList) {
            this.OrgList = [];
          }
          if (this.OrgList.length === 0 && this.newOrgList) {
            this.OrgList = Array.from(this.newOrgList);
          } else {
            this.OrgList = this.processListChanges(
              this.OrgList,
              this.newOrgList,
            );
          }
          this.OrgList = Object.assign([], this.OrgList);
          this.orgListItems$.next(this.OrgList);
          return this.OrgList;
        },
        (e) => {
          console.log("orgsvc failed to get user orgs promise, error: ", e);
          throw e;
        },
      );
  }

  // *** Add, update, delete operations

  addListItem(new_data: any) {
    this.OrgList.push(new_data);
    // some angular libraries require breaking the array reference
    // to pick up the update in the array and trigger change detection.
    // In that case, you can do fo
    this.OrgList = Object.assign([], this.OrgList);
  }

  async addOrganizationPromise(orgdata: OrgData) {
    // Add the org from orgdata (json) on the server
    const jdata = JSON.stringify(orgdata);
    return this.http
      .post(environment.apiUrl + "/org", jdata)
      .toPromise()
      .then(
        (x: OrgData) => {
          console.log("orgsvc add org x=: ", x);
          // Next update the OrgList item
          this.addListItem(x);
          // Update List Behavior Subject
          this.orgListItems$.next(this.OrgList);
          // Update BehaviorSubject
          this.orgItem$.next(x);
          // return data
          return x as OrgData;
        },
        (e) => {
          console.log("orgsvc failed to create org: ", jdata, " error:", e);
          throw e;
        },
      );
  }

  OLDupdateListItem(new_data: any) {
    const idx = this.OrgList.findIndex((obj) => obj.uid === new_data.uid);
    this.OrgList[idx] = new_data;
    // some angular libraries require breaking the array reference
    // to pick up the update in the array and trigger change detection.
    // In that case, you can do fo
    this.OrgList = Object.assign([], this.OrgList);
  }

  async updateOrganizationPromise(oid: string, orgdata: OrgData) {
    // Add the org user from orgdata (json) on the server
    const jdata = JSON.stringify(orgdata);
    return this.http
      .put(environment.apiUrl + "/org/uid/" + oid, jdata)
      .toPromise()
      .then(
        (x: OrgData) => {
          console.log("orgsvc edit org x=: ", x);
          // Next update the OrgList item
          const idx = this.OrgList.findIndex((obj) => obj.uid === x.uid);
          if (idx >= 0 && idx < this.OrgList.length) {
            const old = this.OrgList[idx];
            this.updateListItem(old, x);
          }
          // Update List Behavior Subject
          this.orgListItems$.next(this.OrgList);
          // Update Observable
          this.orgItem$.next(x);
          // return data
          return x as OrgData;
        },
        (e) => {
          console.log("orgsvc failed to edit org: ", jdata, " error:", e);
          throw e;
        },
      );
  }

  OLDremoveListItem(del_uid: any) {
    const idx = this.OrgList.findIndex((obj) => obj.uid === del_uid);
    this.OrgList.splice(idx, 1);
    this.OrgList = Object.assign([], this.OrgList);
  }

  // Remove Organization
  removeOrganizationPromise(uid): any {
    // remove the organization record
    return this.http
      .delete(environment.apiUrl + "/org/uid/" + uid)
      .toPromise()
      .then(
        (x) => {
          console.log("orgsvc updt doc  x=: ", x);
          // Next remove the DocumentList item
          this.removeListItem(x, this.OrgList);
          // Update List Subject/Behavior Subject
          this.orgListItems$.next(this.OrgList);
          // Update BehaviorSubject
          // this.orgItem$.next( x );
          return x;
        },
        (e) => {
          console.error("orgsvc remove org uid =", uid);
          console.error("orgsvc remove org error=", e);
          throw e;
        },
      );
  }

  // *** Stats

  getOrgSystemCount$() {
    this.http
      .get<CountData>(this.baseUrl + "/org/count/system")
      .subscribe((cnt) => {
        console.log("org system cnt =", cnt);
        this.orgSystemCountSubj$.next(cnt);
        return cnt as CountData;
      });
  }

  getOrgSystemCountObs$() {
    return this.http.get<CountData>(this.baseUrl + "/org/count/system");
  }

  getOrgStatusCounts$() {
    this.http
      .get<StatData[]>(this.baseUrl + "/org/count/status")
      .subscribe((cnt) => {
        console.log("org status cnts =", cnt);
        this.orgStatusCountSubj$.next(cnt);
        return cnt as StatData[];
      });
  }

  // STATS BY ORG ID

  getOrgStorage$(oid: string): Observable<SumData> {
    return this.http.get<SumData>(
      this.baseUrl + "/document/storage/org/" + oid,
    );
  }

  getOrgStats(oid: string) {
    if (oid != null) {
      this.orgUserCount$ = this.orgusersvc.getOrgUserCount$(oid);
      this.orgEmpCount$ = this.orgusersvc.getOrgEmpRecordCount$(oid);
      this.orgActiveEmpCount$ = this.orgusersvc.getOrgActiveEmpCount$(oid);
      this.orgAvailableEmpCount$ =
        this.orgusersvc.getOrgAvailableEmpCount$(oid);
      this.orgGuestCount$ = this.orgusersvc.getOrgGuestRecordCount$(oid);
      this.orgActiveGuestCount$ = this.orgusersvc.getOrgActiveGuestCount$(oid);
      this.orgAvailableGuestCount$ =
        this.orgusersvc.getOrgAvailableGuestCount$(oid);
      this.orgGroupCount$ = this.groupsvc.getOrgGroupCount$(oid);
      this.orgDocCount$ = this.documentsvc.getOrgDocumentCount$(oid);
      this.orgStorage$ = this.documentsvc.getOrgDocumentStorage$(oid);
    }
  }
}
